<template>
  <div
    class="shadow-lg rounded-md flex flex-col p-1 min-w-64 text-sm !border !border-neutral-300 bg-white dark:bg-neutral-900 dark:!border-neutral-700"
    :style="{
      top: top + 'px',
      left: left + 'px',
      marginLeft: `${positionOffset.left}px`,
      marginTop: `${positionOffset.top}px`,
    }"
    ref="contextWrapper"
  >
    <ContextMenuItem
      :class="{ disabled: !selectedParagraph }"
      @click="openComments"
    >
      Comment
    </ContextMenuItem>
    <ContextMenuItem
      :class="{ disabled: !selectedParagraph || disableBookmark }"
      @click="toggleBookmark"
    >
      <template v-if="isBookmarked" #icon>
        <IconBookmarkOff size="1rem" />
      </template>
      {{ isBookmarked ? "Delete Bookmark" : "Bookmark" }}
    </ContextMenuItem>
    <ContextMenuItem
      v-if="!isHighlighted"
      :class="{ disabled: !selection?.toString() || disableHighlight }"
      @click="addHighlight"
    >
      Highlight
    </ContextMenuItem>
    <ContextMenuItem
      v-else
      @click="editHighlight"
      :class="{ disabled: disableHighlight }"
    >
      Edit Highlight{{ selectionHasMultipleHighlights ? "s" : "" }}
    </ContextMenuItem>
    <ContextMenuItem
      v-if="isHighlighted"
      @click="removeHighlight"
      :class="{ disabled: disableHighlight }"
    >
      Delete Highlight{{ selectionHasMultipleHighlights ? "s" : "" }}
    </ContextMenuItem>
    <NamiDivider class="!my-1" />
    <ContextMenuItem
      @click="settingsModel.showCommentIcons = !settingsModel.showCommentIcons"
    >
      <template #icon v-if="settingsModel.showCommentIcons">
        <IconCheck size="1rem" />
      </template>
      Show Comment Icons
    </ContextMenuItem>
    <ContextMenuItem
      @click="
        settingsModel.showBookmarkIcons = !settingsModel.showBookmarkIcons
      "
    >
      <template #icon v-if="settingsModel.showBookmarkIcons">
        <IconCheck size="1rem" />
      </template>
      Show Bookmark Icons
    </ContextMenuItem>
    <ContextMenuItem
      @click="settingsModel.showHighlights = !settingsModel.showHighlights"
    >
      <template #icon v-if="settingsModel.showHighlights">
        <IconCheck size="1rem" />
      </template>
      Show Highlights
    </ContextMenuItem>
    <!--    <NamiDivider class="!my-1"/>-->
    <!--    <ContextMenuItem :class="{disabled: !selectedElement.id}">-->
    <!--      Share-->
    <!--    </ContextMenuItem>-->
  </div>
</template>

<script setup lang="ts">
import { IconCheck, IconBookmarkOff } from "@tabler/icons-vue";
import type { NovelSettingsType } from "~/src/novel/reader";
import {
  BOOKMARK_POSITION,
  isNovelParagraph,
  type NovelBookmarkEntity,
} from "~/src/api";
import {
  containsHighlight,
  highlightBetween,
} from "~/src/novel/reader/highlight";

const {
  shownCommentId,
  selection,
  selectedElement,
  chapterBookmarks,
  chapterHighlights,
  selectedParagraph,
  root,
  left,
  top,
} = defineProps<{
  selectedElement: Element;
  selectedParagraph?: Element | null;
  selection?: Selection | null;
  left?: number;
  top?: number;
  root?: Element;
  shownCommentId?: string;

  chapterBookmarks?: NovelBookmarkEntity[];
  chapterHighlights?: NovelBookmarkEntity[];
  disableBookmark?: boolean;
  disableHighlight?: boolean;
}>();

const emit = defineEmits<{
  (e: "highlight", highlightIds?: string[]): void;
  (e: "bookmark", paragraphId: string): void;
  (e: "comment", id: string): void;
  (e: "editBookmarks", bookmark: NovelBookmarkEntity[]): void;
  (e: "editHighlights", highlights: NovelBookmarkEntity[]): void;
  (e: "close"): void;
}>();

const app = useNuxtApp().$app();

const contextWrapper = useTemplateRef("contextWrapper");
const positionOffset = reactive({
  top: 0,
  left: 0,
});

const settingsModel = defineModel<NovelSettingsType>("settings", {
  required: true,
});

const paragraphBookmarkMap = computed(() => {
  if (!chapterBookmarks) return {};
  return Object.fromEntries(
    chapterBookmarks
      .map((bm) => {
        if (bm.attributes.position !== BOOKMARK_POSITION) return;
        const relPara = bm.relationships.find(isNovelParagraph)?.id;
        if (!relPara) return;
        return [relPara, bm];
      })
      .filter(onlyTruthys),
  ) as Record<string, NovelBookmarkEntity>;
});
const isBookmarked = computed(
  () => !!paragraphBookmarkMap.value[selectedParagraph?.id ?? ""],
);
const selectionHasMultipleHighlights = ref(false);

function openComments() {
  if (shownCommentId === selectedElement.id) {
    emit("close");
    return;
  }

  const paragraph = selectedElement.closest<HTMLParagraphElement>("p[id]");
  if (!paragraph) return;

  paragraph.setAttribute("hl", "");
  emit("comment", paragraph.id);
}

const isHighlighted = ref(false);

function toggleBookmark() {
  if (selectedParagraph?.id) emit("bookmark", selectedParagraph.id);
}

function addHighlight() {
  if (!selection || selection.toString().length <= 1) return;
  emit("highlight");
}

function editHighlight() {
  if (!chapterHighlights) return;

  if (root && selection) {
    const hids = containsHighlight(selection, root);
    if (hids?.size) {
      const highlights = chapterHighlights.filter((h) => hids.has(h.id));
      if (!highlights.length) return;
      emit("editHighlights", highlights);
    }
  } else if (selectedParagraph) {
    const hid = highlightBetween(selectedElement, selectedParagraph);
    const highlight = chapterHighlights.find((h) => h.id === hid);
    if (!highlight) return;
    emit("editHighlights", [highlight]);
  }
}

function removeHighlight() {
  if (root && selection) {
    const hids = containsHighlight(selection, root);

    if (hids?.size) {
      emit("highlight", Array.from(hids));
      return;
    }
  }

  if (selectedParagraph) {
    const hid = highlightBetween(selectedElement, selectedParagraph);
    emit("highlight", [hid].filter(onlyTruthys));
  }
}

watch(
  () => [root, selection, selectedElement, selectedParagraph],
  () => {
    if (root && selection) {
      const hids = containsHighlight(selection, root);
      isHighlighted.value = !!hids?.size;
    }
    checkForHighlightWithin();
    onResize();
  },
);

watch(
  () => [left, top],
  () => onResize(),
);

const checkForHighlightWithin = () => {
  if (selectedParagraph)
    isHighlighted.value ||= !!highlightBetween(
      selectedElement,
      selectedParagraph,
    );
};

const onResize = () => {
  if (!contextWrapper.value) return;
  positionOffset.left = 0;
  positionOffset.top = 0;

  nextTick(() => {
    const rect = contextWrapper.value?.getBoundingClientRect();
    if (rect) {
      const { left, top, width, height } = rect;
      if (left + width > window.innerWidth) {
        positionOffset.left = window.innerWidth - (left + width);
      }
      if (top + height > window.innerHeight) {
        positionOffset.top = window.innerHeight - (top + height);
      }
    }
  });
};

const checkForMultipleHighlights = () => {
  if (!root || !selection) return;
  const hids = containsHighlight(selection, root);
  selectionHasMultipleHighlights.value = !!hids && hids.size > 1;
};

const confirmRemoveHighlight = async (multiple: boolean) => {
  return (
    (await app?.prompt(
      multiple
        ? "Are you sure you want to remove these highlights?"
        : "Are you sure you want to remove this highlight?",
      {
        icon: "confused",
        buttons: {
          cancel: {
            buttonType: "secondary",
            buttonText: "Cancel",
          },
          confirm: {
            buttonType: "danger",
            buttonText: "Remove",
          },
        },
      },
    )) === "confirm"
  );
};

onMounted(() => {
  checkForHighlightWithin();
  onResize();
});

onUpdated(() => {
  checkForMultipleHighlights();
});

onBeforeUnmount(() => {
  // observer?.disconnect();
});
</script>

<style scoped lang="postcss"></style>
